import { useState, useCallback, useEffect } from 'react';
import { ArrowLeft, Clock, Plus, Star } from 'lucide-react';
import { useGerArquivos } from '../../hooks/useGerArquivos';
import { FiltrosArquivos } from '../gerArquivos/FiltrosArquivos';
import { ListaArquivos } from '../gerArquivos/ListaArquivos';
import { PaginacaoArquivos } from '../gerArquivos/PaginacaoArquivos';
import { NovoArquivoPage } from '../gerArquivos/NovoArquivoPage';
import { FavoritosPage } from '../gerArquivos/FavoritosPage';
import { ErroDisplay } from '../gerArquivos/ErroDisplay';
import { AbrirArquivoLoading } from '../gerArquivos/AbrirArquivoLoading';
import { Arquivo } from '../../types/gerArquivos';
import { sendToTerminal } from '../../lib/terminalLogger';
import { corelCommunication } from '../../lib/corel/communication';
import { useNotifications } from '../feedback/NotificationProvider';
import { useAbrirArquivoFlow } from '../../hooks/useAbrirArquivoFlow';
import { useUnifiedPermissions } from '../../hooks/useUnifiedPermissions';
import { PermissionDenied } from '../ui';

interface GerArquivosPageProps {
  onBack: () => void;
}

export function GerArquivosPageRefatorada({ onBack }: GerArquivosPageProps) {
  const [showNovoArquivoPage, setShowNovoArquivoPage] = useState(false);
  const [showFavoritosPage, setShowFavoritosPage] = useState(false);
  const [arquivoAbrindo, setArquivoAbrindo] = useState<Arquivo | null>(null);

  // 🛡️ SEGURANÇA: Validação via permissionConfig (admin sempre tem acesso)
  const { checkFeature } = useUnifiedPermissions();
  const permission = checkFeature('general.can_manage_files');

  const {
    arquivos,
    paginacao,
    filtros,
    carregando,
    erro,
    atualizarFiltros,
    alternarFavorito,
    recarregarArquivos,
    irParaPagina
  } = useGerArquivos();
  const { notify } = useNotifications();

  // Hook para gerenciar o fluxo de abertura de arquivo
  const { abrirArquivo: abrirArquivoFlow, carregando: abrindoArquivo, stage } = useAbrirArquivoFlow(
    // onSuccess
    (arquivo) => {
      setArquivoAbrindo(null);
      notify({ type: 'success', message: `Arquivo "${arquivo.arquivoNome}" aberto com sucesso` });
    },
    // onError
    (arquivo, errorMsg) => {
      setArquivoAbrindo(null);
      notify({ type: 'error', message: `Erro ao abrir arquivo: ${errorMsg}` });
    }
  );

  const handleAbrirArquivo = useCallback(async (arquivo: Arquivo) => {
    setArquivoAbrindo(arquivo);
    await abrirArquivoFlow(arquivo);
  }, [abrirArquivoFlow]);

  const handleAbrirDiretorio = useCallback(async (arquivo: Arquivo) => {
    try { sendToTerminal('GERARQ:ABRIR_DIR_REQUEST', { id: arquivo.id, dirbase: arquivo.dirbase }); } catch {}
    try {
      if (!arquivo.dirbase) {
        notify({ type: 'warning', message: 'Diretório não disponível para este arquivo' });
        return;
      }
      
      // Enviar comando para abrir pasta no Windows via Corel
      await new Promise<any>((resolve, reject) => {
        const reqId = corelCommunication.sendActionGuarded('abrir-pasta', { 
          caminho: arquivo.dirbase 
        }, (resp: any) => {
          if (resp?.status === 'ok') {
            resolve(resp);
          } else {
            reject(new Error(resp?.message || 'Falha ao abrir pasta'));
          }
        });
        if (!reqId) reject(new Error('WebView indisponível'));
      });
      
      try { sendToTerminal('GERARQ:ABRIR_DIR_OK', { id: arquivo.id, dirbase: arquivo.dirbase }); } catch {}
      notify({ type: 'success', message: 'Pasta aberta no Windows' });
    } catch (e) {
      try { sendToTerminal('GERARQ:ABRIR_DIR_ERROR', { id: arquivo.id, error: e instanceof Error ? e.message : String(e) }); } catch {}
      notify({ type: 'error', message: 'Erro ao abrir pasta no Windows' });
    }
  }, [notify]);

  const handleToggleFavorito = useCallback(async (arquivo: Arquivo) => {
    try {
      await alternarFavorito(arquivo.id);
      notify({ type: 'success', message: arquivo.favorito ? 'Removido dos favoritos' : 'Adicionado aos favoritos' });
    } catch {
      notify({ type: 'error', title: 'Favorito', message: 'Não foi possível atualizar favorito', sticky: true });
    }
  }, [alternarFavorito, notify]);

  const handleNovoArquivo = useCallback(() => {
    try { sendToTerminal('GERARQ:NOVO_OPEN'); } catch {}
    setShowNovoArquivoPage(true);
  }, []);

  const handleNovoArquivoVoltar = useCallback(() => {
    setShowNovoArquivoPage(false);
  }, []);

  const handleNovoArquivoSucesso = useCallback(async () => {
    try { sendToTerminal('GERARQ:SAVE_SUCCESS_CALLBACK'); } catch {}
    setShowNovoArquivoPage(false);
    await recarregarArquivos();
  }, [recarregarArquivos]);

  const handleFiltrosChange = useCallback((novosFiltros: Partial<typeof filtros>) => {
    try { sendToTerminal('GERARQ:FILTROS_CHANGE', novosFiltros); } catch {}
    atualizarFiltros(novosFiltros);
  }, [atualizarFiltros]);

  const handlePaginaChange = useCallback((pagina: number) => {
    try { sendToTerminal('GERARQ:PAGINACAO_CHANGE', { pagina }); } catch {}
    irParaPagina(pagina);
  }, [irParaPagina]);

  const handleRetry = useCallback(async () => {
    try { sendToTerminal('GERARQ:LIST_RETRY'); } catch {}
    await recarregarArquivos();
  }, [recarregarArquivos]);

  const temFiltrosAtivos = filtros.searchTerm || filtros.dateFrom || filtros.dateTo || filtros.showAllUsers;

  useEffect(() => { try { sendToTerminal('GERARQ:MOUNT'); } catch {} }, []);

  // 🛡️ SEGURANÇA: Guard de permissão
  if (!permission.allowed) {
    return (
      <PermissionDenied 
        status={permission.status}
        title="Gerenciador de Arquivos"
        onBack={onBack}
      />
    );
  }

  // Se estiver na página de novo arquivo, renderizar apenas ela
  if (showNovoArquivoPage) {
    return (
      <NovoArquivoPage
        onVoltar={handleNovoArquivoVoltar}
        onSalvoComSucesso={handleNovoArquivoSucesso}
      />
    );
  }

  // Se estiver na página de favoritos, renderizar apenas ela
  if (showFavoritosPage) {
    return (
      <FavoritosPage
        onBack={() => {
          setShowFavoritosPage(false);
          sendToTerminal('GERARQ:FAVORITOS_BACK');
        }}
      />
    );
  }

  return (
    <div className="h-full flex flex-col bg-navy-900">
      {/* Loading overlay durante abertura de arquivo */}
      <AbrirArquivoLoading 
        arquivoNome={arquivoAbrindo?.arquivoNome || ''} 
        visible={abrindoArquivo && arquivoAbrindo !== null} 
        stage={stage}
      />

      {/* Header */}
      <div className="flex items-center gap-2 p-3 border-b border-navy-700">
        <button onClick={onBack} className="p-1.5 text-gray-400 hover:text-white transition-colors flex-shrink-0" title="Voltar" aria-label="Voltar">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <Clock className="w-5 h-5 text-blue-400 flex-shrink-0" />
        <h1 className="text-lg font-semibold text-white truncate flex-1">Gerenciar Arquivos</h1>
        <button
          onClick={() => {
            setShowFavoritosPage(true);
            sendToTerminal('GERARQ:FAVORITOS_OPEN');
          }}
          className="p-1.5 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-400/10 rounded-md transition-all flex-shrink-0"
          title="Ver favoritos"
          aria-label="Ver favoritos"
        >
          <Star className="w-5 h-5 fill-current" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 p-3 overflow-hidden flex flex-col">
        {/* Filtros */}
        <div className="mb-2 flex-shrink-0">
          <FiltrosArquivos filtros={filtros} onFiltrosChange={handleFiltrosChange} carregando={carregando} />
        </div>

        {/* Erro Display */}
        {erro && (
          <div className="mb-4">
            <ErroDisplay erro={erro} onRetry={handleRetry} />
          </div>
        )}

        {/* Lista de Arquivos */}
        <div className="flex-1 overflow-y-auto">
          <ListaArquivos arquivos={arquivos} carregando={!!carregando} onToggleFavorito={handleToggleFavorito} onAbrirArquivo={handleAbrirArquivo} onAbrirDiretorio={handleAbrirDiretorio} temFiltrosAtivos={!!temFiltrosAtivos} />
        </div>

        {/* Paginação */}
        <PaginacaoArquivos paginacao={paginacao} onPaginaChange={handlePaginaChange} carregando={carregando} />
      </div>

      {/* Footer com botão Novo */}
      <div className="p-2 border-t border-navy-700">
        <button onClick={handleNovoArquivo} disabled={carregando} className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed" title="Criar novo arquivo" aria-label="Criar novo arquivo">
          <Plus className="w-3.5 h-3.5" />
          <span>Novo Arquivo</span>
        </button>
      </div>
    </div>
  );
}
